const MCP_RETURN_TO_PARAM = 'mcp_return_to';
const ALLOWLIST_ENV_VAR = 'REACT_APP_MCP_RETURN_TO_ALLOWLIST';
const ENV_VAR = 'REACT_APP_ENV';
const LOCAL_ENVS = new Set(['', 'local', 'development']);

const readEnv = (key: string): string | undefined => {
  if (typeof window !== 'undefined') {
    const fromWindow = (window as unknown as Record<string, unknown>)[key];
    if (typeof fromWindow === 'string') return fromWindow;
  }
  const fromProcess = process.env[key];
  return typeof fromProcess === 'string' ? fromProcess : undefined;
};

export const parseMcpReturnToAllowlist = (raw: unknown): string[] => {
  if (typeof raw !== 'string' || raw.length === 0) return [];
  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
};

export const getMcpReturnToAllowlist = (): string[] => {
  return parseMcpReturnToAllowlist(readEnv(ALLOWLIST_ENV_VAR));
};

export const readMcpReturnToFromSearch = (search: string): string | null => {
  const params = new URLSearchParams(search);
  const value = params.get(MCP_RETURN_TO_PARAM);
  return value && value.length > 0 ? value : null;
};

/**
 * Validates a candidate `mcp_return_to` against an allowlist of fully-qualified
 * URLs.
 *
 * Security model (intentionally strict):
 *  - The candidate must be a parseable absolute URL.
 *  - It must NOT contain a fragment.
 *  - It must NOT contain userinfo (`user:pass@host`).
 *  - Its `origin + pathname` must `===` an allowlist entry (case-sensitive,
 *    no trailing-slash normalization, no decoding). The protocol is part of
 *    the origin, so http/https mismatches are rejected automatically.
 *  - The candidate's query string is preserved verbatim when redirecting, but
 *    is NOT considered for the allowlist check (the gateway handoff URL's
 *    query carries dynamic OAuth state — client_id, PKCE, etc.).
 */
export const isAllowedMcpReturnTo = (
  candidate: string,
  allowlist: string[] = getMcpReturnToAllowlist()
): boolean => {
  if (!candidate || allowlist.length === 0) return false;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return false;
  }

  if (parsed.hash) return false;
  if (parsed.username || parsed.password) return false;

  const originPath = `${parsed.origin}${parsed.pathname}`;
  return allowlist.includes(originPath);
};

/**
 * Returns the `mcp_return_to` query value when it is present AND passes the
 * allowlist check. Returns null otherwise (silent when absent, warning when
 * present-but-rejected).
 */
export const getSafeMcpReturnTo = (
  search: string = typeof window !== 'undefined'
    ? window.location.search
    : ''
): string | null => {
  const candidate = readMcpReturnToFromSearch(search);
  if (!candidate) return null;

  if (!isAllowedMcpReturnTo(candidate)) {
    console.warn(
      '[mcpReturnTo] Ignoring mcp_return_to: value is not on the allowlist.'
    );
    return null;
  }

  return candidate;
};

/**
 * If a safe `mcp_return_to` is present on the URL, navigates the browser
 * to it and returns true. Returns false otherwise (the caller should fall
 * through to its existing post-login navigation).
 */
export const redirectToMcpReturnToIfPresent = (): boolean => {
  const target = getSafeMcpReturnTo();
  if (!target) return false;
  window.location.assign(target);
  return true;
};

/**
 * Should be called once at app startup. Warns when the allowlist is missing
 * in a non-local environment so misconfiguration is visible in logs.
 */
export const assertMcpReturnToAllowlistConfigured = (): void => {
  const env = String(readEnv(ENV_VAR) ?? '').toLowerCase();
  if (LOCAL_ENVS.has(env)) return;
  if (getMcpReturnToAllowlist().length === 0) {
    console.warn(
      `[mcpReturnTo] ${ALLOWLIST_ENV_VAR} is not configured for env "${env}"; MCP OAuth handoff redirects will be rejected.`
    );
  }
};
